/**
 * `resoudreCollisionsEtiquettes()` — le placeur d'étiquettes de figure.
 *
 * Il lève la limite assumée de H5 dans
 * `docs/produit/SPEC-REPRESENTATION-VISUELLE.md` : *« `resoudreFigure` émet
 * chaque texte avec son ancrage et un décalage calculé le long de la normale
 * sortante […]. **Pas de solveur de collisions au MVP.** Dégradation acceptée
 * et bornée : sur un triangle très aplati, deux étiquettes peuvent se
 * serrer. »* Deux étiquettes serrées ne mentent pas — `decrireFigure()` porte
 * toujours la même information en mots — mais elles se lisent mal, et une
 * figure qui se lit mal est une figure qui mesure mal.
 *
 * La stratégie est celle, éprouvée, du placement d'étiquettes en cartographie,
 * réduite à ce que la maquette demande :
 *
 *  1. **Positions candidates.** Autour de chaque ancre, huit orientations
 *     (la direction préférée, puis ±45°, ±90°, ±135°, 180°) sur trois anneaux
 *     de dégagement croissant, essayées **dans un ordre fixe**. La candidate
 *     n°0 est EXACTEMENT le placement de la normale sortante : une figure sans
 *     conflit sort donc du solveur **inchangée**, au bit près.
 *  2. **Boîtes englobantes.** Le chevauchement est mesuré entre étiquettes
 *     ET contre les traits de la figure — une étiquette posée sur un côté est
 *     aussi illisible que sur une autre étiquette. Le quadrillage n'est jamais
 *     un obstacle : la maquette écrit ses graduations par-dessus.
 *  3. **Ordre de priorité.** Les étiquettes qui **portent une donnée**
 *     (mesures d'énoncé, inconnue cherchée, angles) sont placées avant les
 *     étiquettes d'identité (les lettres de sommet), elles-mêmes avant les
 *     décoratives. Si quelque chose doit être déplacé loin, ce n'est pas
 *     « 6 cm ».
 *  4. **Filet de rappel.** En dernier recours l'étiquette est posée à l'écart,
 *     sur un anneau lointain, et un trait fin la relie à son ancre — c'est le
 *     `RappelEtiquette` que le renderer dessine.
 *
 * **Déterminisme obligatoire.** Même entrée → même sortie, toujours : c'est ce
 * qui rend la figure identique dans l'app, sur le web et dans le courriel du
 * parent, et ce qui la rend testable. Aucun hasard, aucune horloge, aucune
 * dépendance à l'ordre d'itération d'un objet : ce module ne lit que des
 * tableaux, ne trie qu'avec un comparateur total (priorité, puis index
 * d'origine), et n'emploie ni `Math.random`, ni `Date`, ni `Object.keys`, ni
 * `for…in`, ni `toLocaleString`.
 *
 * **La description textuelle ne bouge jamais avec l'étiquette.**
 * `decrireFigure()` lit `points` et `boite`, jamais `textes` : déplacer une
 * étiquette change la mise en page, pas ce que la figure dit.
 *
 * Convention : repère de TRAVAIL, y vers le HAUT (celui de `geometrie.ts`).
 * Les longueurs sont en unités naturelles de la figure.
 */
/**
 * Priorités de placement. Une étiquette de priorité basse est posée d'abord et
 * garde donc la meilleure position ; ce sont celles qui portent une DONNÉE.
 */
export const PRIORITE_ETIQUETTE = {
    /** Mesure d'énoncé, angle, inconnue cherchée, coordonnées. */
    donnee: 0,
    /** Lettre de sommet, nom de point, nom de vecteur : l'identité de l'objet. */
    identite: 1,
    /** Étiquette de courbe, titre d'axe : utile, jamais indispensable au sens. */
    decor: 2,
};
/* ------------------------------------------------------------- constantes */
/**
 * Les huit orientations, en degrés depuis la direction préférée. L'ordre est
 * NORMATIF : c'est lui qui rend la disposition reproductible. La première est
 * l'identité — une figure sans conflit ressort inchangée.
 */
const ORIENTATIONS_DEG = [0, 45, -45, 90, -90, 135, -135, 180];
/** Anneaux de dégagement, en multiples du dégagement nominal. */
const ANNEAUX_PROCHES = [1, 1.55, 2.2];
/** Anneaux du filet de rappel : l'étiquette est posée À L'ÉCART et reliée par un trait. */
const ANNEAUX_DEPORTES = [3.3, 4.6];
/** Marge de respiration ajoutée autour de chaque boîte pour le test de chevauchement. */
const MARGE_BOITE = 0.14;
/** Coût d'un trait de figure traversant la boîte, rapporté au coût d'un recouvrement total. */
const COUT_TRAIT = 0.55;
/** Préférence pour les premières candidates : départage sans jamais renverser un vrai conflit. */
const COUT_RANG = 1e-4;
/** En deçà, la position est considérée libre. */
const EPS = 1e-9;
/* --------------------------------------------------------------- géométrie */
function alignementDe(u) {
    // Règle littérale du solveur historique : elle décide de quel côté de
    // l'ancre le texte s'écrit, et le renderer applique exactement la même.
    return u.x > 0.3 ? 'debut' : u.x < -0.3 ? 'fin' : 'milieu';
}
function boiteDe(position, alignement, e) {
    const minX = alignement === 'debut'
        ? position.x
        : alignement === 'fin'
            ? position.x - e.largeur
            : position.x - e.largeur / 2;
    return {
        minX,
        maxX: minX + e.largeur,
        minY: position.y - e.versLeBas,
        maxY: position.y + e.versLeHaut,
    };
}
function dilater(b, m) {
    return { minX: b.minX - m, maxX: b.maxX + m, minY: b.minY - m, maxY: b.maxY + m };
}
function aire(b) {
    return Math.max(0, b.maxX - b.minX) * Math.max(0, b.maxY - b.minY);
}
function recouvrement(a, b) {
    const dx = Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX);
    const dy = Math.min(a.maxY, b.maxY) - Math.max(a.minY, b.minY);
    return dx > 0 && dy > 0 ? dx * dy : 0;
}
/** Le segment traverse-t-il la boîte ? (extrémité dedans, ou coupe une des quatre arêtes.) */
export function segmentCoupeBoite(s, b) {
    const [p, q] = s;
    const dedans = (r) => r.x >= b.minX && r.x <= b.maxX && r.y >= b.minY && r.y <= b.maxY;
    if (dedans(p) || dedans(q))
        return true;
    const coins = [
        { x: b.minX, y: b.minY },
        { x: b.maxX, y: b.minY },
        { x: b.maxX, y: b.maxY },
        { x: b.minX, y: b.maxY },
    ];
    for (let i = 0; i < 4; i += 1) {
        const a1 = coins[i];
        const a2 = coins[(i + 1) % 4];
        if (segmentsSeCoupent(p, q, a1, a2))
            return true;
    }
    return false;
}
function orientation(a, b, c) {
    const v = (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
    return v > EPS ? 1 : v < -EPS ? -1 : 0;
}
function surSegment(a, b, p) {
    return (Math.min(a.x, b.x) - EPS <= p.x &&
        p.x <= Math.max(a.x, b.x) + EPS &&
        Math.min(a.y, b.y) - EPS <= p.y &&
        p.y <= Math.max(a.y, b.y) + EPS);
}
function segmentsSeCoupent(p1, p2, q1, q2) {
    const o1 = orientation(p1, p2, q1);
    const o2 = orientation(p1, p2, q2);
    const o3 = orientation(q1, q2, p1);
    const o4 = orientation(q1, q2, p2);
    if (o1 !== o2 && o3 !== o4)
        return true;
    if (o1 === 0 && surSegment(p1, p2, q1))
        return true;
    if (o2 === 0 && surSegment(p1, p2, q2))
        return true;
    if (o3 === 0 && surSegment(q1, q2, p1))
        return true;
    if (o4 === 0 && surSegment(q1, q2, p2))
        return true;
    return false;
}
/** Point du bord de la boîte le plus proche de l'ancre — le pied du filet de rappel. */
function pointDeRappel(b, ancre) {
    return {
        x: Math.min(Math.max(ancre.x, b.minX), b.maxX),
        y: Math.min(Math.max(ancre.y, b.minY), b.maxY),
    };
}
/**
 * Toutes les positions candidates d'une étiquette, dans l'ordre normatif
 * (anneau par anneau, orientation par orientation). Exportée pour le test de
 * déterminisme : c'est cette liste, et son ORDRE, qui font la reproductibilité.
 */
export function candidatesEtiquette(e) {
    // `direction` est déjà unitaire quand elle vient du solveur de figures. On
    // ne la renormalise alors PAS : une division par un `n` valant 1 au bit près
    // décalerait la candidate n°0 d'un ULP, et la promesse « une figure sans
    // conflit ressort inchangée » ne tiendrait plus au bit près.
    const n = Math.hypot(e.direction.x, e.direction.y);
    const dejaUnitaire = Math.abs(n - 1) <= 1e-12;
    const ux = n < 1e-9 ? 0 : dejaUnitaire ? e.direction.x : e.direction.x / n;
    const uy = n < 1e-9 ? 1 : dejaUnitaire ? e.direction.y : e.direction.y / n;
    const sortie = [];
    const anneaux = [
        ...ANNEAUX_PROCHES.map((r) => [r, false]),
        ...ANNEAUX_DEPORTES.map((r) => [r, true]),
    ];
    for (const [facteur, deportee] of anneaux) {
        for (const angle of ORIENTATIONS_DEG) {
            const a = (angle * Math.PI) / 180;
            const c = Math.cos(a);
            const s = Math.sin(a);
            const dx = ux * c - uy * s;
            const dy = ux * s + uy * c;
            const d = e.degagement * facteur;
            const position = { x: e.ancre.x + dx * d, y: e.ancre.y + dy * d };
            const alignement = alignementDe({ x: dx, y: dy });
            sortie.push({ position, alignement, boite: boiteDe(position, alignement, e), deportee });
        }
    }
    return sortie;
}
/**
 * Place les étiquettes sans les faire se chevaucher, ni chevaucher les traits.
 *
 * @param etiquettes Dans l'ordre du tracé. **L'ordre du tableau rendu est
 * exactement celui-ci** : le solveur change des positions, jamais l'ordre ni le
 * contenu des textes.
 * @param obstacles Les traits de la figure (jamais le quadrillage).
 * @param reserves Boîtes déjà occupées et INAMOVIBLES — graduations d'axe,
 * catégories d'un diagramme, étiquettes de la couche du dessous.
 */
export function resoudreCollisionsEtiquettes(etiquettes, obstacles, reserves = []) {
    const placements = etiquettes.map(() => null);
    const occupees = reserves.map((b) => b);
    // Boîte englobante de chaque trait, calculée UNE fois. Une courbe échantillonnée
    // apporte des centaines de segments ; le rejet grossier en écarte l'immense
    // majorité avant le test exact, et c'est ce qui garde le placeur assez
    // rapide pour être rejoué à chaque cran de curseur, sur n'importe quel
    // appareil (invariant 8 : on optimise la fluidité, jamais le tracé).
    const traits = obstacles.map((s) => ({
        s,
        minX: Math.min(s[0].x, s[1].x),
        maxX: Math.max(s[0].x, s[1].x),
        minY: Math.min(s[0].y, s[1].y),
        maxY: Math.max(s[0].y, s[1].y),
    }));
    // Ordre de traitement : priorité croissante, puis index d'origine. Le second
    // critère rend le tri TOTAL, donc indépendant de la stabilité du tri du
    // moteur JavaScript — c'est une condition du déterminisme, pas un détail.
    const ordre = etiquettes
        .map((e, i) => ({ i, priorite: e.priorite }))
        .sort((a, b) => (a.priorite !== b.priorite ? a.priorite - b.priorite : a.i - b.i))
        .map((x) => x.i);
    for (const i of ordre) {
        const e = etiquettes[i];
        const candidates = candidatesEtiquette(e);
        const aireEtiquette = Math.max(aire(candidates[0]?.boite ?? { minX: 0, maxX: 1, minY: 0, maxY: 1 }), 1e-9);
        let meilleure = candidates[0];
        let meilleurCout = Number.POSITIVE_INFINITY;
        for (let rang = 0; rang < candidates.length; rang += 1) {
            const c = candidates[rang];
            const dilatee = dilater(c.boite, e.degagement * MARGE_BOITE);
            let cout = rang * COUT_RANG;
            for (const b of occupees)
                cout += recouvrement(dilatee, b) / aireEtiquette;
            for (const t of traits) {
                if (t.maxX < c.boite.minX || t.minX > c.boite.maxX)
                    continue;
                if (t.maxY < c.boite.minY || t.minY > c.boite.maxY)
                    continue;
                if (segmentCoupeBoite(t.s, c.boite)) {
                    cout += COUT_TRAIT;
                    if (cout > meilleurCout)
                        break;
                }
            }
            if (cout < meilleurCout) {
                meilleurCout = cout;
                meilleure = c;
            }
            // Libre à la marge du rang près : inutile d'aller plus loin, l'ordre est normatif.
            if (cout <= rang * COUT_RANG + EPS)
                break;
        }
        occupees.push(dilater(meilleure.boite, e.degagement * MARGE_BOITE));
        placements[i] = {
            position: meilleure.position,
            alignement: meilleure.alignement,
            deportee: meilleure.deportee,
            rappel: meilleure.deportee ? [e.ancre, pointDeRappel(meilleure.boite, e.ancre)] : null,
        };
    }
    return placements.map((p, i) => p ?? {
        position: etiquettes[i].ancre,
        alignement: 'milieu',
        deportee: false,
        rappel: null,
    });
}
//# sourceMappingURL=etiquettes.js.map